/**
 * screens/detail/overviewPanel.tsx — Queue Detail → Overview body (rev 5).
 * Faithful port of the prototype's qdetail Overview: the intake→engine→output
 * contract strip, decision Signals (tag + text + 6 metrics), the live neutral
 * line, the resource/lease pool (assign × presence), the dark "Consuming
 * surfaces" card, and the append-only activity ledger. Pure renderer — the
 * parent (queueDetail) fetches GET /v1/queues/:id/overview and passes it in.
 */

import React from 'react';
import { View, Text, ScrollView } from 'react-native';
import { useTheme } from '../../context/themeContext';
import { Mono, StateChip } from '../../queue/components/atoms';
import { useResponsive } from '../../lib/responsive';
import { fmtWait } from '../../queue/format';
import type { OverviewView } from '../../api/queueDtos';
import type { EntryState } from '../../queue/types';

const SIGNAL_LABEL: Record<string, string> = { HEALTHY: 'Healthy', BUILDING: 'Building', AT_CAPACITY: 'At capacity' };
const SIGNAL_UPPER: Record<string, string> = { HEALTHY: 'HEALTHY', BUILDING: 'BUILDING', AT_CAPACITY: 'AT CAPACITY' };

function fmtMin(seconds: number): string {
  const m = Math.max(0, Math.round(seconds / 60));
  if (m <= 0) return '0m';
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

export default function OverviewPanel({ data, product }: { data: OverviewView | null; product: string }) {
  const { theme } = useTheme();
  const { colors } = theme;
  const { width } = useResponsive();
  const narrow = width < 900;

  if (!data) {
    return (
      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', paddingTop: 60 }}>
        <Text style={{ color: colors.ink4 }}>Loading overview…</Text>
      </View>
    );
  }

  const s = data.signals;
  const poolLabel = (data.queue.vocab?.resourcePl || data.queue.vocab?.resource || (data.queue.matchType === 'FITTED' ? 'tables' : 'slots')) as string;
  // Singular lease noun for the line's Lease column — vocab-driven so a Chat
  // queue reads "agent" and a Voice queue "call slot" (fallback "slot").
  const leaseNoun = (data.queue.vocab?.resource || 'slot') as string;

  const stats = [
    { k: 'Utilisation', v: `${Math.round(s.utilisation * 100)}%` },
    { k: 'Peak in line · today', v: String(s.peakInLineToday) },
    { k: 'Max wait · today', v: fmtMin(s.maxWaitTodaySeconds) },
    { k: 'Served · today', v: String(s.servedToday) },
    { k: data.queue.matchType === 'FITTED' ? 'No-shows' : 'Abandoned', v: String(s.abandonedToday) },
    { k: 'Avg wait now', v: fmtMin(s.avgWaitNowSeconds) },
  ];

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 28 }}>
      {/* ── Contract strip: intake → engine → output ── */}
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginVertical: 4, backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: 14, overflow: 'hidden' }}>
        <View style={{ flex: 1, minWidth: 220, padding: 18, borderRightWidth: 1, borderRightColor: colors.line }}>
          <SectionKicker>1 · Captured at intake</SectionKicker>
          <View style={{ gap: 6, marginTop: 9 }}>
            {data.contract.intake.map((f, i) => (
              <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 7 }}>
                <View style={{ width: 4, height: 4, borderRadius: 2, backgroundColor: colors.ink }} />
                <Text style={{ fontSize: 12.5, color: colors.ink2 }}>{f}</Text>
              </View>
            ))}
          </View>
        </View>
        <View style={{ flex: 1, minWidth: 220, padding: 18, borderRightWidth: 1, borderRightColor: colors.line, backgroundColor: colors.paper }}>
          <SectionKicker>2 · Engine allocates</SectionKicker>
          <Text style={{ fontSize: 12.5, color: colors.ink2, lineHeight: 19, marginTop: 9 }}>{data.contract.ruleTitle}</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
            {data.contract.config.map((c) => (
              <View key={c.k} style={{ borderWidth: 1, borderColor: colors.line, borderRadius: 5, paddingHorizontal: 6, paddingVertical: 2 }}>
                <Mono style={{ fontSize: 10, color: colors.ink3 }}>{c.k} {c.v}</Mono>
              </View>
            ))}
          </View>
        </View>
        <View style={{ flex: 1, minWidth: 220, padding: 18 }}>
          <SectionKicker>3 · Output → consumer</SectionKicker>
          <Text style={{ fontSize: 13, fontWeight: '600', color: colors.ink, marginTop: 9 }}>{data.contract.consumer}</Text>
          <Text style={{ fontSize: 11.5, color: colors.ink4, marginTop: 3, lineHeight: 16 }}>{data.contract.gets}</Text>
        </View>
      </View>

      {/* ── Signals · today ── */}
      <View style={{ backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: 14, overflow: 'hidden', marginTop: 16 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, borderBottomWidth: 1, borderBottomColor: colors.line, flexWrap: 'wrap' }}>
          <Text style={{ fontSize: 12, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.6, color: colors.ink3 }}>Signals · today</Text>
          <SignalTag signal={data.signal} />
          <Text style={{ fontSize: 12.5, color: colors.ink2, flex: 1 }}>{data.decisionText}</Text>
        </View>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
          {stats.map((st) => (
            <View key={st.k} style={{ flexGrow: 1, minWidth: 120, padding: 14, borderRightWidth: 1, borderRightColor: colors.paper2, borderBottomWidth: 1, borderBottomColor: colors.paper2 }}>
              <Mono style={{ fontSize: 22, fontWeight: '600', lineHeight: 24, color: colors.ink }}>{st.v}</Mono>
              <Text style={{ fontSize: 10.5, color: colors.ink4, textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 5 }}>{st.k}</Text>
            </View>
          ))}
        </View>
      </View>

      {/* ── Line (1.6) + Pool & Consuming surfaces (1) ── */}
      <View style={{ flexDirection: narrow ? 'column' : 'row', gap: 16, alignItems: 'flex-start', marginTop: 16 }}>
        {/* The live neutral line */}
        <View style={{ flex: 1.6, minWidth: narrow ? '100%' : 420, width: narrow ? '100%' : undefined, backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: 14, overflow: 'hidden' }}>
          <PanelHeader title="The line · live entries" right="neutral · product-agnostic" />
          {/* column header */}
          <View style={{ flexDirection: 'row', paddingVertical: 8, paddingHorizontal: 16, borderBottomWidth: 1, borderBottomColor: colors.line, backgroundColor: colors.paper }}>
            <Text style={colHead(colors, 44)}>Tok</Text>
            <Text style={[colHead(colors), { flex: 1 }]}>Entry</Text>
            <Text style={[colHead(colors, 88)]}>State</Text>
            <Text style={[colHead(colors, 56), { textAlign: 'center' }]}>Pos</Text>
            <Text style={[colHead(colors, 52), { textAlign: 'center' }]}>Lease</Text>
            <Text style={[colHead(colors, 52), { textAlign: 'right' }]}>Waited</Text>
          </View>
          <View style={{ maxHeight: 420 }}>
            <ScrollView>
              {data.line.length === 0 ? (
                <Text style={{ padding: 40, textAlign: 'center', color: colors.ink4, fontSize: 13 }}>No one in the line.</Text>
              ) : data.line.map((r) => (
                <View key={r.entryId} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 11, paddingHorizontal: 16, borderBottomWidth: 1, borderBottomColor: colors.paper2 }}>
                  <Mono style={{ width: 44, fontSize: 11, color: colors.ink3 }}>#{r.token}</Mono>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={{ fontSize: 12.5, fontWeight: '600', color: colors.ink }} numberOfLines={1}>{r.name ?? '—'}</Text>
                    <Mono style={{ fontSize: 10.5, color: colors.ink4 }}>{r.maskedPhone ?? '••••'} · {r.channel}</Mono>
                  </View>
                  <View style={{ width: 88 }}><StateChip state={r.state as EntryState} compact /></View>
                  <Mono style={{ width: 56, textAlign: 'center', color: colors.ink3, fontSize: 12 }}>{r.position != null ? `#${r.position}` : 'live'}</Mono>
                  <Mono style={{ width: 52, textAlign: 'center', fontWeight: '600', fontSize: 12, color: colors.ink }}>{data.queue.matchType === 'FITTED' ? '—' : leaseNoun}</Mono>
                  <Mono style={{ width: 52, textAlign: 'right', color: colors.ink4, fontSize: 12 }}>{fmtWait(Math.round(r.waitedSeconds / 60))}</Mono>
                </View>
              ))}
            </ScrollView>
          </View>
        </View>

        {/* Pool + consuming surfaces */}
        <View style={{ flex: 1, minWidth: narrow ? '100%' : 300, width: narrow ? '100%' : undefined, gap: 16 }}>
          <View style={{ backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: 14, overflow: 'hidden' }}>
            <PanelHeader title={`${poolLabel} pool`} right="assign × presence" />
            <View style={{ maxHeight: 230 }}>
              <ScrollView>
                {data.resourcePool.length === 0 ? (
                  <Text style={{ padding: 20, fontSize: 12.5, color: colors.ink4 }}>
                    {data.queue.matchType === 'FITTED' ? 'Tables live in SK Calendar — add tables on the Host Console floor.' : 'No slots yet.'}
                  </Text>
                ) : data.resourcePool.map((p) => (
                  <View key={p.resourceId} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 10, paddingHorizontal: 16, borderBottomWidth: 1, borderBottomColor: colors.paper2 }}>
                    <Text style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: '600', color: colors.ink }} numberOfLines={1}>{p.label ?? p.externalRef?.slice(0, 8) ?? '—'}</Text>
                    <Mono style={{ fontSize: 10, color: colors.ink4 }}>{p.leaseKind === 'AGENT' ? `${p.currentLoad}/${p.capacity}` : String(p.capacity)}</Mono>
                    <AssignBadge state={p.assignmentState} colors={colors} />
                    <OpBadge state={p.operationalState} colors={colors} />
                  </View>
                ))}
              </ScrollView>
            </View>
          </View>
          {/* "Consuming surfaces" preview card removed from Overview — the product-
              surface previews (Guest/Host/Now-Serving/…) are reachable from the
              topbar PREVIEW launcher instead, so they aren't duplicated here. */}
        </View>
      </View>

      {/* ── Activity ledger ── */}
      <View style={{ backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: 14, overflow: 'hidden', marginTop: 16 }}>
        <PanelHeader title="Activity ledger" right="append-only" />
        {data.ledger.length === 0 ? (
          <Text style={{ padding: 26, textAlign: 'center', color: colors.ink4, fontSize: 12.5 }}>
            {data.queue.matchType === 'FITTED' ? 'No activity yet.' : "POOL monitor — events stream in the vertical's live monitor."}
          </Text>
        ) : (
          <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
            {data.ledger.map((h, i) => (
              <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 10, paddingHorizontal: 16, borderBottomWidth: 1, borderBottomColor: colors.paper2, width: narrow ? '100%' : '50%' }}>
                <Mono style={{ fontSize: 11, color: colors.ink4, width: 42 }}>{new Date(h.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}</Mono>
                <ActorBadge actor={h.actor} colors={colors} />
                <Text style={{ fontSize: 12.5, fontWeight: '600', color: colors.ink }}>{h.type}</Text>
                <Text style={{ fontSize: 12, color: colors.ink3, flex: 1, minWidth: 0 }} numberOfLines={1}>{h.name ?? ''}</Text>
                <Mono style={{ fontSize: 10, color: colors.ink4 }}>#{h.token}</Mono>
              </View>
            ))}
          </View>
        )}
      </View>
    </ScrollView>
  );
}

function SectionKicker({ children }: { children: React.ReactNode }) {
  const { theme } = useTheme(); const { colors } = theme;
  return <Text style={{ fontSize: 10, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.6, color: colors.ink4 }}>{children}</Text>;
}

function PanelHeader({ title, right }: { title: string; right: string }) {
  const { theme } = useTheme(); const { colors } = theme;
  return (
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 13, paddingHorizontal: 16, borderBottomWidth: 1, borderBottomColor: colors.line }}>
      <Text style={{ fontSize: 12, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.6, color: colors.ink3 }}>{title}</Text>
      <Mono style={{ fontSize: 11, color: colors.ink4 }}>{right}</Mono>
    </View>
  );
}

function colHead(colors: any, width?: number) {
  return { width, fontSize: 10, fontWeight: '600' as const, textTransform: 'uppercase' as const, letterSpacing: 0.4, color: colors.ink4 };
}

function SignalTag({ signal }: { signal: string }) {
  const { theme } = useTheme(); const { colors } = theme;
  const solid = signal === 'AT_CAPACITY';
  const outline = signal === 'BUILDING';
  return (
    <View style={{ paddingHorizontal: 8, paddingVertical: 2, borderRadius: 5, backgroundColor: solid ? colors.ink : colors.card, borderWidth: outline ? 1 : solid ? 0 : 1, borderColor: solid ? colors.ink : outline ? colors.ink : colors.lineStrong }}>
      <Mono style={{ fontSize: 10, fontWeight: '600', color: solid ? colors.white : outline ? colors.ink : colors.ink3 }}>{SIGNAL_UPPER[signal] ?? signal}</Mono>
    </View>
  );
}

function AssignBadge({ state, colors }: { state: string; colors: any }) {
  const occ = state === 'OCCUPIED', held = state === 'HELD';
  return (
    <View style={{ paddingHorizontal: 7, paddingVertical: 1, borderRadius: 5, backgroundColor: occ ? colors.ink : held ? colors.paper2 : colors.card, borderWidth: occ ? 0 : 1, borderColor: held ? colors.ink : colors.lineStrong, borderStyle: held ? 'dashed' : 'solid' }}>
      <Mono style={{ fontSize: 10, fontWeight: '600', color: occ ? colors.white : colors.ink2 }}>{state}</Mono>
    </View>
  );
}

function OpBadge({ state, colors }: { state: string; colors: any }) {
  const off = state !== 'ONLINE';
  return (
    <View style={{ paddingHorizontal: 7, paddingVertical: 1, borderRadius: 5, backgroundColor: off ? colors.paper3 : colors.card, borderWidth: 1, borderColor: off ? colors.lineStrong : colors.line, borderStyle: off ? 'dashed' : 'solid' }}>
      <Mono style={{ fontSize: 10, fontWeight: '600', color: off ? colors.ink4 : colors.ink3 }}>{state}</Mono>
    </View>
  );
}

function ActorBadge({ actor, colors }: { actor: string; colors: any }) {
  const sys = actor === 'SYSTEM', staff = actor === 'STAFF';
  return (
    <View style={{ paddingHorizontal: 6, paddingVertical: 1, borderRadius: 4, backgroundColor: sys ? colors.ink : staff ? colors.paper2 : colors.card, borderWidth: sys ? 0 : 1, borderColor: colors.lineStrong }}>
      <Mono style={{ fontSize: 10, fontWeight: '600', color: sys ? colors.white : colors.ink3 }}>{actor}</Mono>
    </View>
  );
}
